"use client";

import { useState } from "react";
import { motion } from "motion/react";
import CommonSchema from "@/components/common/CommonSchema";
import {
  Sparkles,
  ShieldAlert,
  ArrowRight,
  Tag,
  ChevronLeft,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { FadeInSection } from "@/components/common/FadeInSection";
import { IOffer } from "@/slices/offerSlice";
import { cleanHeaderTags, isValidImgSrc } from "@/_helper/helper";
import { OfferInquiryForm } from "@/components/offers/OfferInquiryForm";
import OfferInquiryModal from "@/components/offers/OfferInquiryModal";

interface OfferDetailClientProps {
  offer: IOffer;
  allOffers?: IOffer[];
}

export default function OfferDetailClient({
  offer,
  allOffers = [],
}: OfferDetailClientProps) {
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState<boolean>(false);
  const title = offer?.Title || offer?.MetaTitle || "";

  // Use offer image for the Hero Banner background
  const bgImage = isValidImgSrc(offer?.BannerImage)
    ? offer.BannerImage!
    : isValidImgSrc(offer?.Images)
      ? offer.Images!
      : "";

  const shortDesc = offer?.ShortDescription
    ? cleanHeaderTags(offer.ShortDescription)
    : "";

  const longDesc = offer?.LongDescription
    ? cleanHeaderTags(offer.LongDescription)
    : "";

  const terms = offer?.TermsAndCondition
    ? cleanHeaderTags(offer.TermsAndCondition)
    : "";

  // Other active offers for recommendation section
  const otherOffers = allOffers
    .filter(
      (o) =>
        o?.ID !== offer?.ID && (o?.URL !== offer?.URL || o?.Url !== offer?.Url),
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Hero Banner with Embedded Inquiry Form (matching Landing Page Banner) */}
      <section className="relative w-full min-h-[100vh] lg:min-h-[720px] pt-28 pb-10 sm:pt-36 sm:pb-14 flex flex-col justify-end overflow-hidden bg-[#1f1a16] text-white">
        {/* Background Image: Full visibility with clean presentation */}
        <div className="absolute inset-0 z-0">
          <Image
            src={isValidImgSrc(bgImage) ? bgImage : "/imports/placeholder.png"}
            alt={title || "Sarasiruham Special Offer"}
            fill
            priority
            className="object-cover object-center"
            sizes="100vw"
          />

          {/* Gradient strictly at the bottom where the text is placed */}
          <div className="absolute inset-x-0 bottom-0 h-[60%] sm:h-[50%] md:h-[45%] bg-gradient-to-t from-black/90 via-black/70 to-transparent pointer-events-none" />
        </div>

        {/* Hero Content Container */}
        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end w-full">
            {/* Left Column: Embedded Inquiry Form Card (matching Landing Page Banner) */}
            <div className="lg:col-span-6 xl:col-span-5 w-full order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <OfferInquiryForm offerTitle={title} offerId={offer?.ID} />
              </motion.div>
            </div>

            {/* Right Column: Hero Heading & Tagline positioned at bottom in desktop view */}
            <div className="lg:col-span-6 xl:col-span-7 flex flex-col justify-end text-center lg:text-left order-1 lg:order-2 lg:self-end lg:pb-3">
              <Link
                href="/offers"
                className="inline-flex items-center gap-2 text-[#d4af37] mb-4 hover:gap-3 transition-all self-center lg:self-start"
              >
                <ChevronLeft className="w-5 h-5" />
                <span style={{ fontFamily: "Jost, sans-serif" }}>
                  Back to Special Offers
                </span>
              </Link>

              <motion.h1
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-white tracking-wide leading-[1.12] mb-3 sm:mb-4 font-cormorant drop-shadow-xl"
                style={{ fontFamily: "var(--font-cormorant-family), serif" }}
              >
                {title}
              </motion.h1>

              {shortDesc && (
                <motion.p
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.25 }}
                  className="text-base sm:text-lg md:text-xl text-white/95 font-light tracking-wide max-w-2xl font-jost drop-shadow-md mx-auto lg:mx-0"
                  style={{ fontFamily: "var(--font-jost-family), sans-serif" }}
                >
                  {shortDesc}
                </motion.p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area - Compact Padding & High Contrast Large Typography */}
      <section className="py-8 md:py-12 relative">
        {/* Subtle background glow accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a961]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5a6f3e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 space-y-6">
          {/* Main CMS Long Description Content Section */}
          <FadeInSection>
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg border border-[#2a2520]/10 space-y-6">
              {/* Header Badge Row */}
              <div className="flex items-center justify-between gap-4 border-b border-[#2a2520]/10 pb-4 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c9a961]/15 text-[#2a2520] text-xs font-bold uppercase tracking-widest">
                  <Sparkles className="w-3.5 h-3.5 text-[#c9a961]" />
                  OFFER DETAILS & PACKAGE INCLUSIONS
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#f5f1ec] text-[#2a2520] text-xs font-semibold">
                  <Tag className="w-3.5 h-3.5 text-[#c9a961]" />
                  <span>SPECIAL OFFER</span>
                </div>
              </div>

              {/* Render CMS Long Description with Large, Dark, High-Contrast Typography */}
              <div
                className="blog-content"
                style={{ fontFamily: "Jost, sans-serif" }}
                dangerouslySetInnerHTML={{ __html: longDesc }}
              />

              {/* Compact Callout Action Bar */}
              <div className="pt-6 border-t border-[#2a2520]/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h4
                    className="text-2xl text-[#111111] font-bold"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    Interested in this Offer?
                  </h4>
                  <p
                    className="text-sm text-[#333333] mt-0.5"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Get instant assistance & reserve your luxury package at
                    Sarasiruham Resort.
                  </p>
                </div>

                <motion.button
                  type="button"
                  onClick={() => {
                    const formEl =
                      document.getElementById("offer-inquiry-form");
                    if (formEl) {
                      formEl.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    } else {
                      setIsInquiryModalOpen(true);
                    }
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto bg-[#2a2520] hover:bg-[#c9a961] text-white py-3 px-7 rounded-full font-semibold text-sm transition-colors duration-300 shadow-md flex items-center justify-center gap-2 cursor-pointer"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  <span>ENQUIRE NOW</span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </FadeInSection>

          {/* CMS Terms and Conditions Content Section */}
          {terms && (
            <FadeInSection delay={0.1}>
              <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-md border border-[#2a2520]/10 space-y-3">
                <div className="flex items-center gap-2 text-[#111111] text-sm font-bold uppercase tracking-wider">
                  <ShieldAlert className="w-4 h-4 text-[#c9a961]" />
                  <span>TERMS & CONDITIONS</span>
                </div>

                <div className="border-t border-[#2a2520]/10 pt-3">
                  <div
                    className="blog-content"
                    style={{ fontFamily: "Jost, sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: terms }}
                  />
                </div>
              </div>
            </FadeInSection>
          )}

          {/* Recommended Offers Section */}
          {otherOffers && otherOffers?.length > 0 ? (
            <div className="pt-8 border-t border-[#2a2520]/10">
              <FadeInSection>
                <div className="text-center mb-6">
                  <h3
                    className="text-2xl sm:text-3xl text-[#111111] font-bold"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    More Special Offers
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {otherOffers.map((item, idx) => {
                    const itemTitle = item?.Title || "Special Package";
                    const itemUrl =
                      item?.URL || item?.Url || `offer-${item?.ID}`;
                    const itemImg = isValidImgSrc(item?.Images)
                      ? item.Images!
                      : isValidImgSrc(item?.BannerImage)
                        ? item.BannerImage!
                        : "/imports/placeholder.png";

                    return (
                      <Link
                        key={item?.ID || idx}
                        href={`/offer/${itemUrl}`}
                        className="block group"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden shadow-md group-hover:shadow-xl border border-[#2a2520]/10 transition-all duration-300 h-full flex flex-col justify-between">
                          <div>
                            <div className="relative h-44 w-full overflow-hidden">
                              <Image
                                src={itemImg}
                                alt={itemTitle}
                                fill
                                className="object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div className="p-5">
                              <h4
                                className="text-xl text-[#111111] font-semibold group-hover:text-[#c9a961] transition-colors leading-tight"
                                style={{
                                  fontFamily: "Cormorant Garamond, serif",
                                }}
                              >
                                {itemTitle}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </FadeInSection>
            </div>
          ) : null}
        </div>
      </section>

      {/* Inquiry Form Popup Modal */}
      <OfferInquiryModal
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
        offerTitle={title}
        offerId={offer?.ID}
      />
    </div>
  );
}
